import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 12, second half: adding a sequence up, counting without listing, the
 * expansion that turns out to be a counting problem, and the distribution that
 * turns out to be the expansion.
 *
 * These four lessons are ordered as one argument rather than four topics. The
 * binomial coefficients in `binomial-theorem` are the combinations from
 * `combinatorics-intro` — not numbers that resemble them, the same numbers for
 * the same reason — and the binomial distribution in
 * `probability-distributions` is those coefficients again, now weighted by
 * probability. A student who notices that has learned one thing three times
 * instead of three things once.
 *
 * `series` opens the half and does the heavy lifting for the course as a whole,
 * because the infinite geometric sum is where a student first agrees that a
 * process with no last step can still have an exact answer. `limits` later
 * names that agreement. Taught as a formula to memorise, it names nothing.
 *
 * `mathematical-induction` follows `series` immediately, and deliberately
 * proves the formula the student was handed there. It is the only place in the
 * course where a result they already use is put on a footing, and it works
 * because the debt is fresh: Gauss's pairing convinces, and a lesson that opens
 * by asking whether the formula still holds at n = 10¹⁰⁰ is asking about
 * something the student has actually relied on. Placed anywhere else, induction
 * is a proof technique in search of a claim.
 */
export const DISCRETE_LESSONS: Lesson[] = [
  lesson({
    id: 'series-01',
    unitId: 'unit-series',
    conceptId: 'series',
    title: 'Series and Summation',
    summary: 'What happens when a sequence is added up, including one that is never finished.',
    objectives: [
      'Tell a sequence apart from a series, and a term apart from a sum',
      'Sum an arithmetic and a geometric series of n terms',
      'Decide whether an infinite geometric series has a total, and find it',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A story about a schoolboy',
        blocks: [
          text('The story is probably embroidered, but the mathematics in it is not. A teacher, wanting quiet, sets the class to add up every whole number from 1 to 100. A boy called Gauss puts his slate down almost immediately with 5050 on it.'),
          text('He had not added a hundred numbers. He had noticed that the list pairs off:'),
          math('1 + 100 = 101, \\quad 2 + 99 = 101, \\quad 3 + 98 = 101, \\; \\ldots'),
          text('There are 50 such pairs, each worth 101, and 50 × 101 = 5050.'),
          insight('That trick is the whole of this lesson in miniature. A **sum** of many terms need not be computed term by term — if the terms have structure, the total has a formula, and the formula does not care how many terms there are.'),
        ],
      },
      intuition: {
        title: 'Sequence, series, and the two formulas',
        blocks: [
          text('A **sequence** is a list: 3, 7, 11, 15, … A **series** is that list added up: 3 + 7 + 11 + 15 + …'),
          warning('The nth term and the sum of n terms are two different questions with two different formulas, and the answers are rarely close. For 3, 7, 11, 15 the fourth term is 15 and the sum of four terms is 36.', 'A term is not a total'),
          text('Gauss’s pairing works on any **arithmetic** series — one with a constant difference — because pairing the first with the last, the second with the second-last and so on always gives the same total. That gives:'),
          math('S_{n} = \\frac{n}{2}\\left(a_{1} + a_{n}\\right)'),
          text('Read it as "the number of terms, times the average of the first and last". A **geometric** series — one with a constant *ratio* — needs a different argument, and gets a different formula:'),
          math('S_{n} = \\frac{a_{1}\\left(1 - r^{n}\\right)}{1 - r}'),
          warning('The ratio is found by *dividing* consecutive terms, not by subtracting them. Subtraction is how you find the common difference of an arithmetic sequence, and using it here produces a number that has nothing to do with the series.', 'Ratio, not difference'),
          text('Count the terms carefully, too. From the 4th term to the 10th there are seven terms, not six — both ends are included, and reading n straight off the last index is the standard way to be one out.'),
          text('**Sigma notation** writes a sum without the dots. The letter below is where the counter starts, the number above is where it stops:'),
          math('\\sum_{k=1}^{5} 2k = 2 + 4 + 6 + 8 + 10 = 30'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Now the case that matters most. Add halves forever: 1/2 + 1/4 + 1/8 + 1/16 + …'),
          figure(
            {
              kind: 'number-line',
              from: 0,
              to: 1,
              step: 0.25,
              marks: [
                { at: 0.5, label: '1/2' },
                { at: 0.75, label: '3/4' },
                { at: 0.875, label: '7/8' },
                { at: 1, label: '1' },
              ],
            },
            'The running total after one, two and three terms. Each step covers half of what is left, so the total never passes 1 — and never stops short of it either.',
          ),
          insight('Endlessly many terms does not mean an endless total. When |r| < 1 each term is a fixed fraction of the one before, r^n shrinks to nothing, and the sum formula collapses to a single fraction:'),
          math('S_{\\infty} = \\frac{a_{1}}{1 - r}, \\qquad |r| < 1'),
          text('For halves, a₁ = 1/2 and r = 1/2, so the total is exactly 1. Not approximately 1, and not 1 in the limit of patience — exactly 1.'),
          warning('The condition is not decoration. With |r| ≥ 1 the terms do not shrink, the running total grows without bound, and the formula returns a number that means nothing. 1 + 2 + 4 + 8 + … has no total, and the formula would cheerfully report −1.', 'Only when the terms shrink'),
          tip('Hold on to what you just agreed to. A sum with no last term has an exact value, decided by where the running total is heading rather than where it arrives. That is a limit, and you will meet it under that name at the end of this course.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A ball is dropped from 2 m and rebounds to 60% of its height each bounce. How far does it travel in total?'),
          steps([
            { label: 'The first drop is not part of any pair.', latex: '2\\text{ m}' },
            { label: 'Every bounce after that is travelled twice — up and down.', latex: '2(1.2 + 0.72 + 0.432 + \\ldots)' },
            { label: 'Inside the bracket is geometric.', latex: 'a_{1} = 1.2, \\quad r = 0.6', note: '0.72 ÷ 1.2 = 0.6 — divide, do not subtract.' },
            { label: 'Sum it, since |r| < 1.', latex: '\\frac{1.2}{1 - 0.6} = \\frac{1.2}{0.4} = 3' },
            { label: 'Double it and add the first drop.', latex: '2 + 2(3) = 8\\text{ m}' },
          ]),
          insight('The ball bounces infinitely many times and travels 8 metres. Both of those are true, and the second is not an approximation — which is only strange if you expect infinitely many things to add to infinity.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('series', 4)] },
      independent: { title: 'On your own', blocks: [practice('series', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('series', 3, 'Infinite sums, and deciding whether there is one.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A sequence is a list; a series is that list added up.',
            'The nth term and the sum of n terms answer different questions.',
            'Arithmetic: Sₙ = (n/2)(a₁ + aₙ) — count times the average of the ends.',
            'Geometric: find r by dividing, never by subtracting.',
            'An infinite geometric series totals a₁/(1 − r), but only when |r| < 1.',
            'Count the terms; the last index is not the count unless you started at 1.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'mathematical-induction-01',
    unitId: 'unit-series',
    conceptId: 'mathematical-induction',
    title: 'Proof by Induction',
    summary: 'Settling infinitely many claims at once, with an argument that fits on a page.',
    objectives: [
      'Say why checking cases can never prove a claim about every whole number',
      'Write the base case and the inductive step of a proof',
      'State P(k + 1) correctly, and use the assumption P(k) to reach it',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A formula you believed without proof',
        blocks: [
          text('Gauss’s pairing gave you a formula for adding the whole numbers up to n:'),
          math('1 + 2 + 3 + \\cdots + n = \\frac{n(n + 1)}{2}'),
          text('It works at n = 100. It works at n = 7. Check it at n = 1, 2, 3, 4, 5 and it works every time. Does it work at n = 10¹⁰⁰?'),
          text('You believe so. But every check you have made was one check, and there are infinitely many values of n. No number of checks gets through them all — a list that never ends cannot be finished, however fast you work.'),
          warning('Checking cases is evidence, not proof, and the evidence can be badly misleading. n² + n + 41 is prime at n = 0, at n = 1, and at every n up to 39. At n = 40 it is 41², which is not prime. Thirty-nine cases in a row proved nothing.', 'Where checking cases goes wrong'),
          insight('**Induction** settles infinitely many claims with a finite argument. It is the standard answer to "but how do you know it keeps working?", and it is about a page long however large the claim.'),
        ],
      },
      intuition: {
        title: 'Dominoes',
        blocks: [
          text('Stand a row of dominoes on end, stretching out of sight. You want to know that every one of them falls. Two facts are enough, and neither requires watching them all:'),
          list([
            'The first one falls.',
            'Whenever a domino falls, it knocks over the next one.',
          ], true),
          text('Given both, every domino falls — the first knocks the second, which knocks the third, and there is no domino the argument fails to reach. Neither fact alone is enough. Push the first with no chain and only one falls; build a perfect chain and never push, and none do.'),
          insight('A proof by induction is exactly those two facts about a statement P(n).\n\n**Base case:** show P(1) is true.\n\n**Inductive step:** show that *if* P(k) is true, *then* P(k + 1) is true.\n\nTogether they give P(n) for every whole number n from 1 upwards.'),
          warning('The step alone proves nothing, however airtight it is. "If n = n + 1 then n + 1 = n + 2" is a perfectly valid step, and the claim it carries is false everywhere — because nothing ever makes it true to begin with. The base case is what the step has to start from.', 'Do not skip the base case'),
          warning('In the step you assume P(k) for one value k, not P(n) for every n. Assuming the claim for every n and then deriving it is assuming what you set out to prove, which is the one move a proof may never make.', 'Assume it once, not everywhere'),
          text('Almost all the work is in stating P(k + 1) correctly. P(k + 1) is the original claim with k + 1 written everywhere n was. For the sum formula:'),
          math('P(k): \\; 1 + 2 + \\cdots + k = \\frac{k(k + 1)}{2}'),
          math('P(k + 1): \\; 1 + 2 + \\cdots + k + (k + 1) = \\frac{(k + 1)(k + 2)}{2}'),
          tip('Substitute into the right-hand side rather than trying to guess it. n(n + 1)/2 with n replaced by k + 1 is (k + 1)((k + 1) + 1)/2, which tidies to (k + 1)(k + 2)/2. Leaving a k in place restates P(k), and a proof that gets from P(k) to P(k) has gone nowhere.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'array', rows: 4, columns: 5, label: '4 × 5' },
            'Why the formula is true at n = 4, seen rather than argued: a staircase of 1 + 2 + 3 + 4 dots and a second staircase upside down fit together into this 4 × 5 rectangle. The sum is half of 4 × 5, which is n(n + 1)/2.',
          ),
          text('The picture is convincing and it is not a proof of the general claim — it is a proof for n = 4. Redrawing it at n = 5 proves n = 5. Induction is what turns "I could redraw this for any n" into an argument that does not have to be redrawn.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Prove that 1 + 2 + 3 + ⋯ + n = n(n + 1)/2 for every whole number n ≥ 1.'),
          steps([
            { label: 'State the claim.', latex: 'P(n): \\; \\sum_{i=1}^{n} i = \\frac{n(n + 1)}{2}' },
            { label: 'Base case: check n = 1.', latex: '1 = \\frac{1 \\times 2}{2} = 1 \\;\\checkmark', note: 'The left side is the single term 1; the right side is 1. The first domino falls.' },
            { label: 'Assume P(k) for some k ≥ 1.', latex: '1 + 2 + \\cdots + k = \\frac{k(k + 1)}{2}', note: 'One value of k, assumed — not every n.' },
            { label: 'Write the target, P(k + 1).', latex: '1 + 2 + \\cdots + k + (k + 1) = \\frac{(k + 1)(k + 2)}{2}' },
            { label: 'Start from the left side of P(k + 1) and use the assumption.', latex: '\\frac{k(k + 1)}{2} + (k + 1)', note: 'The first k terms are replaced by the assumed total — this is the only place the assumption is used, and every induction proof has such a place.' },
            { label: 'Take out the common factor.', latex: '(k + 1)\\left(\\frac{k}{2} + 1\\right) = (k + 1)\\cdot\\frac{k + 2}{2}' },
            { label: 'Which is the right side of P(k + 1).', latex: '\\frac{(k + 1)(k + 2)}{2} \\;\\checkmark' },
            { label: 'Conclude.', latex: 'P(n) \\text{ holds for every } n \\geq 1', note: 'True at 1, and each case carries the next: the dominoes are set and pushed.' },
          ]),
          insight('The proof never mentions a particular large n, and that is the point. It is not a faster way of checking cases — it is an argument that makes checking unnecessary, which is the only thing that could ever settle a claim about infinitely many numbers.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('mathematical-induction', 4)] },
      independent: { title: 'On your own', blocks: [practice('mathematical-induction', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('mathematical-induction', 3, 'Stating the next case, and finding what a broken proof is missing.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Checking cases is evidence; it can never cover infinitely many.',
            'A proof by induction has two parts: the base case and the inductive step.',
            'The base case shows P(1) is true. Without it the step carries nothing.',
            'The step assumes P(k) for one k and deduces P(k + 1) from it.',
            'State P(k + 1) by substituting k + 1 everywhere n appeared.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'combinatorics-intro-01',
    unitId: 'unit-counting',
    conceptId: 'combinatorics-intro',
    title: 'Counting',
    summary: 'Answering "how many ways?" without writing any of them down.',
    objectives: [
      'Use the multiplication principle for a choice made in stages',
      'Count arrangements with a permutation',
      'Decide whether order matters, and use a combination when it does not',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A question you cannot answer by listing',
        blocks: [
          text('How many ways are there to deal a five-card hand from a pack of 52?'),
          text('You could list them. At one hand per second, without sleeping, you would finish in about 82 years.'),
          text('The answer is 2 598 960, and it can be had in a line of arithmetic. This lesson is about how — and the honest reason it matters is that probability is built on top of it. "How likely?" is nearly always "how many ways, out of how many ways altogether?", and neither number can be reached by listing.'),
        ],
      },
      intuition: {
        title: 'Multiply the stages, then divide out what you overcounted',
        blocks: [
          text('Start with the one rule everything else is built from. If a choice is made in stages, the counts **multiply**:'),
          math('4 \\text{ shirts} \\times 3 \\text{ trousers} = 12 \\text{ outfits}'),
          warning('They multiply, they do not add. Adding says 4 shirts and 3 trousers make 7 outfits — but each shirt can be worn with every pair of trousers, so each shirt generates three outfits of its own.', 'Stages multiply'),
          text('Now arrange things. Three runners on a podium: 3 choices for gold, then 2 left for silver, then 1 for bronze, so 3 × 2 × 1 = 6 arrangements. Written with a factorial, and for r positions out of n:'),
          math('{}^{n}P_{r} = \\frac{n!}{(n-r)!}'),
          text('Then the question that separates the two halves of this subject. Choose three people from five for a **committee**. The same reasoning gives 5 × 4 × 3 = 60 — but that is wrong, and it is wrong in an exact and repairable way.'),
          insight('Picking Ann then Ben then Cara gives the same committee as picking Cara then Ann then Ben. Every committee has been counted once for each of the 3! = 6 orders its members could have been picked in. So divide by 6: there are 10 committees, not 60.'),
          math('{}^{n}C_{r} = \\frac{n!}{r!\\,(n-r)!}'),
          text('So there is really one question to ask before anything else: **does reordering the result change it?**'),
          table(['The question', 'Order matters?', 'Use'], [
            ['Gold, silver and bronze from 8 runners', 'Yes — the positions differ', 'Permutation'],
            ['A committee of 3 from 8 people', 'No — a committee is a set', 'Combination'],
            ['A 4-digit PIN from 10 digits', 'Yes — 1234 is not 4321', 'Permutation'],
            ['5 cards dealt from 52', 'No — a hand is a hand', 'Combination'],
          ]),
          tip('Test it on the specific case in front of you rather than on the wording. Swap two of the chosen items over. If you are looking at the same outcome, order does not matter.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'array', rows: 4, columns: 3, label: '4 shirts × 3 trousers' },
            'Every dot is one outfit. The rectangle is why stages multiply: each of the 4 rows meets each of the 3 columns exactly once, and none of the 12 cells is empty or repeated.',
          ),
          text('The same picture explains the division. If the two chosen items were interchangeable, the grid would be counting each pair twice — once above the diagonal and once below — and the repair is to halve it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A class of 12 must send 4 students to a conference. In how many ways can the group be chosen?'),
          steps([
            { label: 'Ask whether order matters.', latex: '\\text{No — a group of 4 is a group of 4.}', note: 'So it is a combination.' },
            { label: 'Write the formula.', latex: '{}^{12}C_{4} = \\frac{12!}{4!\\,8!}' },
            { label: 'Cancel the 8! rather than expanding 12!.', latex: '\\frac{12 \\times 11 \\times 10 \\times 9}{4 \\times 3 \\times 2 \\times 1}' },
            { label: 'Evaluate.', latex: '\\frac{11880}{24} = 495' },
          ]),
          text('Had the four been given distinct jobs — chair, secretary, treasurer, delegate — the answer would be the permutation 11 880 instead, exactly 24 times larger. The 24 is the 4! orders each group could be assigned in.'),
          warning('The two answers differ by a factor of r!, which makes the mistake easy to make and easy to check. If your answer is a whole multiple of 24 larger than you expected, you have counted arrangements where you wanted selections.', 'The two answers differ by r!'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('combinatorics-intro', 4)] },
      independent: { title: 'On your own', blocks: [practice('combinatorics-intro', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('combinatorics-intro', 3, 'Deciding between a permutation and a combination.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A choice made in stages multiplies; it never adds.',
            'A permutation counts arrangements: ⁿPᵣ = n!/(n − r)!',
            'A combination counts selections: ⁿCᵣ = n!/(r!(n − r)!)',
            'The only difference is dividing by r!, the orders you overcounted.',
            'Ask whether swapping two chosen items changes the outcome.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'binomial-theorem-01',
    unitId: 'unit-counting',
    conceptId: 'binomial-theorem',
    title: 'The Binomial Theorem',
    summary: 'Expanding a bracket to the twentieth power by counting, rather than by multiplying twenty times.',
    objectives: [
      'Expand (x + y)ⁿ using binomial coefficients',
      'Explain why the coefficients are combinations',
      'Find one particular term of an expansion without expanding the rest',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Where do the numbers come from?',
        blocks: [
          text('You can expand (x + y)³ by hand, and you get:'),
          math('x^{3} + 3x^{2}y + 3xy^{2} + y^{3}'),
          text('The powers are unsurprising — they slide from x³ down to y³. The 3s are the interesting part. Where does a 3 come from, when nothing in the question was 3 except the power?'),
          warning('It is not a decoration to be dropped. Writing x³ + x²y + xy² + y³ is a different and wrong expression: at x = y = 1 the true expansion gives 8, which is 2³, and that one gives 4.', 'The coefficients are not optional'),
        ],
      },
      intuition: {
        title: 'The coefficients are counts',
        blocks: [
          text('Write the product out without collecting anything:'),
          math('(x + y)(x + y)(x + y)'),
          text('Expanding means taking one term from each bracket, in every possible way, and adding the products. A term of x²y comes from taking y in exactly one bracket and x in the other two.'),
          insight('So the coefficient of x²y asks: **in how many ways can I choose which one of the three brackets supplies the y?** Three ways — and that is the 3. The binomial coefficients are combinations, not a pattern that happens to fit.'),
          math('(x + y)^{n} = \\sum_{k=0}^{n} {}^{n}C_{k}\\, x^{n-k} y^{k}'),
          text('Everything about the expansion follows from reading that as a counting statement:'),
          list([
            'There are n + 1 terms, one for each k from 0 to n.',
            'The powers of x and y in each term add to n, because every bracket contributes exactly once.',
            'The coefficients are symmetric, since choosing which brackets give y is the same job as choosing which give x.',
            'Each row of coefficients sums to 2ⁿ — the number of ways to answer "x or y?" once per bracket.',
          ]),
          warning('A power does not distribute across a sum. (x + y)ⁿ is not xⁿ + yⁿ: at x = y = 1 those are 2ⁿ and 2, and everything between them is the middle terms.', 'Not xⁿ + yⁿ'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'pascal-triangle', rows: 6, highlight: [{ row: 3, index: 1 }] },
            'Pascal’s triangle. Each entry is the sum of the two above it, and row n holds the coefficients of (x + y)ⁿ. The marked 3 is ³C₁ — the number of ways to pick which single bracket supplies the y.',
          ),
          text('The addition rule has the same explanation as everything else here. To choose k things from n, either the last item is in your selection — leaving k − 1 to choose from n − 1 — or it is not, leaving k to choose from n − 1. Those two cases are the two entries above.'),
          tip('The triangle is quick for small powers and hopeless for large ones; you would need twenty rows before you could touch (x + y)²⁰. The formula does not care, which is why it is worth having both.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the coefficient of x³ in the expansion of (2x + 1)⁵.'),
          steps([
            { label: 'Write the general term.', latex: '{}^{5}C_{k}\\,(2x)^{5-k}\\,1^{k}' },
            { label: 'x³ needs the power on 2x to be 3.', latex: '5 - k = 3 \\;\\Rightarrow\\; k = 2' },
            { label: 'Evaluate that term.', latex: '{}^{5}C_{2}\\,(2x)^{3} = 10 \\times 8x^{3}' },
            { label: 'Collect the coefficient.', latex: '80' },
          ]),
          insight('The 2 had to be raised to the power as well. Taking the coefficient to be ⁵C₂ = 10 is the standard slip here, and it is out by a factor of 8 — the part of the term that was inside the bracket all along.'),
          warning('The expansion starts at k = 0, so the *first* term has index 0 and the kth term has index k − 1. Asked for the 4th term, use k = 3.', 'Counting the terms'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('binomial-theorem', 4)] },
      independent: { title: 'On your own', blocks: [practice('binomial-theorem', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('binomial-theorem', 3, 'Single terms of larger expansions.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The coefficients of (x + y)ⁿ are the combinations ⁿCₖ.',
            'ⁿCₖ counts which k of the n brackets supply the y.',
            'There are n + 1 terms, and the powers in each add to n.',
            'Row n of Pascal’s triangle sums to 2ⁿ.',
            'The kth term has index k − 1, because the expansion starts at 0.',
            'Anything inside the bracket is raised to the power too.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'probability-distributions-01',
    unitId: 'unit-counting',
    conceptId: 'probability-distributions',
    title: 'Probability Distributions',
    summary: 'Describing a random quantity by the chance of every outcome it has.',
    objectives: [
      'Write the probability distribution of a random variable',
      'Compute an expected value by weighting outcomes with their probabilities',
      'Use the binomial distribution for a fixed number of independent trials',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Not one probability but all of them',
        blocks: [
          text('Toss a coin five times. "What is the probability of three heads?" is a fair question, and you can now answer it — but it is the wrong question to ask first.'),
          text('The better one is: what are the chances of *every* possible number of heads, all at once? Answer that and every question of the first kind is already answered.'),
          insight('A **random variable** is a quantity whose value is decided by chance — here, the number of heads. Its **distribution** is the list of every value it can take together with the probability of each. It is the complete description of the randomness, and nothing about that experiment is left to say once you have it.'),
        ],
      },
      intuition: {
        title: 'The table, its total, and the average',
        blocks: [
          text('For one roll of a fair die the distribution is dull but complete:'),
          table(['Value', '1', '2', '3', '4', '5', '6'], [['Probability', '1/6', '1/6', '1/6', '1/6', '1/6', '1/6']]),
          warning('The probabilities must total exactly 1. Something has to happen, so a total above or below 1 means an outcome has been double-counted or missed — and checking that total is the cheapest error-detector in this subject.', 'The total is always 1'),
          text('The **expected value** is the long-run average of the variable. It is not the average of the outcomes; it is the average of the outcomes *weighted by how likely each is*:'),
          math('E(X) = \\sum x \\cdot P(x)'),
          warning('A rare outcome should pull the average less than a common one. Adding the possible values and dividing by how many there are quietly assumes they are equally likely — true for a fair die, false for almost everything else.', 'Weight by probability'),
          text('Now the distribution that matters most, and it is the last lesson wearing new clothes. Fix n independent trials, each succeeding with probability p. Then:'),
          math('P(X = k) = {}^{n}C_{k}\\, p^{k} (1-p)^{n-k}'),
          text('Every piece of that is something you already have. p^k(1 − p)^(n−k) is the probability of one particular run of k successes and n − k failures. ⁿCₖ counts how many such runs there are, since the successes could fall in any of those positions.'),
          insight('So the binomial distribution is the binomial theorem with probabilities substituted in. The coefficients are the same coefficients, for the same reason: they count which of the trials succeeded.'),
          warning('The trials are the pool you choose from, so it is ⁿCₖ with n on the outside — "which k of the n trials succeeded". ᵏCₙ asks which of the successes are trials, which is not a question about anything.', 'n on the outside'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 1 },
                { label: '1', value: 5 },
                { label: '2', value: 10 },
                { label: '3', value: 10 },
                { label: '4', value: 5 },
                { label: '5', value: 1 },
              ],
              unitLabel: 'ways out of 32',
            },
            'Heads in five tosses of a fair coin. The bars are 1, 5, 10, 10, 5, 1 — row 5 of Pascal’s triangle — and they total 32 = 2⁵, so dividing each by 32 gives the distribution.',
          ),
          text('The shape is the reason extremes feel unlikely. Five heads can happen one way; three heads can happen ten ways. Nothing is preventing five heads — there are simply fewer routes to it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A test has 6 multiple-choice questions, each with 4 options. A student guesses every one. What is the probability of exactly 2 correct, and how many should they expect to get right?'),
          steps([
            { label: 'Identify the trials and the success probability.', latex: 'n = 6, \\quad p = \\tfrac{1}{4}, \\quad k = 2' },
            { label: 'Count the ways 2 of the 6 could be the right ones.', latex: '{}^{6}C_{2} = 15' },
            { label: 'Probability of one such pattern.', latex: '\\left(\\tfrac{1}{4}\\right)^{2}\\left(\\tfrac{3}{4}\\right)^{4} = \\tfrac{81}{4096}' },
            { label: 'Multiply.', latex: '15 \\times \\tfrac{81}{4096} \\approx 0.297' },
            { label: 'The expected number is np.', latex: '6 \\times \\tfrac{1}{4} = 1.5' },
          ]),
          insight('An expected value of 1.5 is not a possible outcome — nobody scores one and a half. It is the average over many attempts, and a mean that no individual can achieve is perfectly ordinary; the average household does not have 1.7 children either.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('probability-distributions', 4)] },
      independent: { title: 'On your own', blocks: [practice('probability-distributions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('probability-distributions', 3, 'Binomial probabilities and expected values.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A distribution lists every value of a random variable with its probability.',
            'Those probabilities always total exactly 1.',
            'E(X) = Σ x·P(x) — weight each outcome by its chance.',
            'Binomial: P(X = k) = ⁿCₖ pᵏ(1 − p)ⁿ⁻ᵏ, with n on the outside.',
            'The coefficients are Pascal’s triangle, counting which trials succeeded.',
            'For a binomial, the expected value is np.',
          ]),
        ],
      },
    },
  }),
];
